import { useState, useContext, useEffect, useSelector } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  InputField,
  Button,
  Spinner,
  ErrorMessage,
  SuccessPopup,
} from "@/common/components";
import { validate } from "@/utils/Validations";
import {
  useGetAccessorTransactionInfoQuery,
  useGetAllAccessProfilesForProfileQuery,
} from "@/service/ProfileService";
import { useApproveOrRejectAccessForAdminMutation } from "@/service/AdminService";
import ViewContactInformation from "@/components/Common/ViewContactInformation";
import ProfileInformation from "@/components/Common/ProfileInformation";

const ApproveNewAccessRequest = () => {
  const {
    debounceErrors,
    debounceState,
    cancel,
    setNavHeader,
    skipToken,
    cloneDeep,
    labels,
    setPageHeader,
    accessProfileTypeCode,
    isRiccAdministrator,
    productSponsorId,
  } = useContext(PrivateContext);
  const [operatorId, setOperatorId] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [contactInfo, setContactInfo] = useState();
  const [payload, setPayload] = useState(skipToken);
  const [dataAccessRules, setDataAccessRules] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [showPopup, setShowPopup] = useState("");
  const [accessError, setAccessError] = useState(false);
  const [accessErrorMessage, setAccessErrorMessage] = useState(false);
  const accessorDetailsFromStore = useSelector(
    (state) => state?.commonReducer?.accessorDetails
  );

  const {
    data: accessProfiles,
    isFetching,
    error,
  } = useGetAllAccessProfilesForProfileQuery();

  const {
    data: currentAccessProfile,
    isFetching: isFetching1,
    error: error1,
  } = useGetAccessorTransactionInfoQuery(payload);

  const [approveAccessApi, { isLoading: isFetching2, error: error2 }] =
    useApproveOrRejectAccessForAdminMutation();

  const doSearch = () => {
    setPayload(operatorId);
  };

  const doApproveOrRejectAccess = (submitType) => {
    const dataAccessRulesList = cloneDeep(dataAccessRules);

    for (let val of dataAccessRulesList) {
      val.requestStatus = submitType;
    }

    const payload = {
      operatorId: currentAccessProfile.operatorId,
      submitType: submitType,
      lastMntOpId: accessorDetailsFromStore?.customUserId,
      lastMntDateTime: currentAccessProfile.lastMntDateTime,
      dataAccessRuleList: dataAccessRulesList,
    };

    approveAccessApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.isInError) {
          setShowPopup(
            submitType === "A"
              ? labels?.approveNewAccessRequest?.approved
              : labels?.approveNewAccessRequest?.rejected
          );
        }
      });
  };

  const validateProductMgfLoggedIn = () => {
    if(isRiccAdministrator){
      return true;
    }
    if (!currentAccessProfile) return false;

    const loggedInRole = accessProfileTypeCode; // "P", "I", "R", "A"
    const loggedInSponsorId = productSponsorId;

    const searchedOperator = currentAccessProfile;
    const searchedRules = searchedOperator.dataAccessRuleList || [];

    // If neither insuranceCompany nor productSponsor is set, assume Ricc Operator/Admin
    if (
      !searchedOperator.insuranceCompany &&
      !searchedOperator.productSponsor
    ) {
      return false;
    }

    // --- 2. Is this searched operator tied to the same Product Mfg group? ---
    const hasMatchingRule = searchedRules.some(
      (rule) =>
        rule.productSponsor && rule.productSponsor.id === loggedInSponsorId
    );

    // --- 3. Role-specific checks (based on logged-in user) ---
    if ((loggedInRole === "P" || loggedInRole === "R") && !hasMatchingRule) {
      return false;
    }

    if (loggedInRole === "I" && !hasMatchingRule) {
      return false;
    }

    // Passed all checks → allow
    return true;
  };

  useEffect(() => {
    //check if the productmgf is logged in or not
    if (currentAccessProfile) {
      const rules = currentAccessProfile.dataAccessRuleList || [];
      const isProductMgfLoggedIn = validateProductMgfLoggedIn();

      if (!isProductMgfLoggedIn) {
        const errors = {
          status: 400,
          data: {
            errors: [
              {
                name: "OPERATOR_ID",
                errorMessage: "You do not have authorization to access that ID",
                severity: "ERROR",
              },
            ],
          },
        };
        setAccessError(true);
        setAccessErrorMessage(errors);
         setContactInfo(null);
    setDataAccessRules([]);
    setSelectedEntity("");
        return;
      }

      setAccessError(false);
      setContactInfo(currentAccessProfile?.contact);
      // Filter only if rules exist
      if(isRiccAdministrator){
        setDataAccessRules(
        cloneDeep(
          rules.filter(
            (e) => e?.requestAccessProfile != null && e?.requestStatus !== "R" 
          )
        )
      );
      }else{
        setDataAccessRules(
        cloneDeep(
          rules.filter(
            (e) => e?.requestAccessProfile != null && e?.requestStatus !== "R" && e.productSponsor.id == productSponsorId
          )
        )
      );
      }
      

      if (currentAccessProfile?.insuranceCompany) {
        setSelectedEntity("I");
      } else if (currentAccessProfile?.productSponsor) {
        setSelectedEntity("P");
      } else {
        setSelectedEntity("R"); // fallback if both null
      }
    }
  }, [currentAccessProfile]);

  useEffect(() => {
    setPageHeader(labels?.approveNewAccessRequest?.title);
    setNavHeader(labels?.navbar?.admin);
  }, []);


  return (
    <section className="container-large py-5">
      {(isFetching || isFetching1 || isFetching2) && <Spinner />}
      <ErrorMessage error={error || error1 || error2} wrapperClassName="mb-3" />
      {accessError && (
        <ErrorMessage error={accessErrorMessage} wrapperClassName="mb-3" />
      )}
      <InputField
        id="operatorId"
        label={labels?.approveNewAccessRequest?.operatorId}
        wrapperClassName="col-md-3"
        value={operatorId}
        change={async (e) => {
          debounceState(e, setOperatorId);
          debounceErrors(
            {
              ...errors,
              operatorId: await validate(e, "internalOperatorId"),
            },
            setErrors
          );
        }}
        touch={() => setTouched({ ...touched, operatorId: true })}
        touched={touched.operatorId}
        error={errors.operatorId}
      />

      <Button
        id="search"
        text={labels?.approveNewAccessRequest?.search}
        wrapperClassName="col-md-2"
        click={doSearch}
        disabled={!operatorId || errors?.operatorId}
      />

      {contactInfo && !error1 && (
        <section className="row m-0 mt-5">
          <section className="col-md-7 ps-0 pe-0 pe-md-3">
            <ProfileInformation
              accessProfiles={accessProfiles}
              currentAccessProfile={currentAccessProfile}
              selectedEntity={selectedEntity}
              dataAccessRules={dataAccessRules}
              setDataAccessRules={setDataAccessRules}
              labels={labels?.approveNewAccessRequest}
              showCurrentAccess={false}
            />
            <div className="d-flex justify-content-end flex-row gap-1 mb-4">
              <Button
                id="reject"
                type="secondary"
                text={labels?.approveNewAccessRequest?.reject}
                wrapperClassName="col-md-4 col-6"
                click={() => doApproveOrRejectAccess("R")}
                disabled={dataAccessRules && !dataAccessRules.length}
              />
              <Button
                id="approve"
                text={labels?.approveNewAccessRequest?.approve}
                wrapperClassName="col-md-4 col-6"
                click={() => doApproveOrRejectAccess("A")}
                disabled={dataAccessRules && !dataAccessRules.length}
              />
            </div>
          </section>
          <section className="col-md-5 pe-0 ps-0 ps-md-3">
            <ViewContactInformation
              contactInfo={contactInfo}
              key={contactInfo}
              labels={labels?.approveNewAccessRequest}
            />
          </section>
        </section>
      )}
      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.approveNewAccessRequest?.continue}
        click={cancel}
        body={`${labels?.approveNewAccessRequest?.requestHasBeen} ${showPopup}.`}
      />
    </section>
  );
};

export default ApproveNewAccessRequest;
